import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  Search,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Loader2,
  BarChart3,
  Shield,
  ChevronDown,
  ChevronUp,
  Eye,
  Mic,
  Play,
  Pause,
  Download,
  FileDown,
  X,
  Volume2,
  FileText,
  Quote,
  CheckSquare,
  XSquare,
  MessageSquare,
  ExternalLink,
  Copy,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TAAdminDashboard() {
  const navigate = useNavigate();
  const [token] = useState(localStorage.getItem("adminToken") || "");
  const [activeTab, setActiveTab] = useState("scoring");
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [scoringDetail, setScoringDetail] = useState(null);
  const [integrityReport, setIntegrityReport] = useState(null);
  const [voiceRecordings, setVoiceRecordings] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailTab, setDetailTab] = useState("scoring");

  useEffect(() => { fetchQueue(); }, []);

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/ta/scoring/queue`);
      const data = await res.json();
      setQueue(data);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const handleTriggerScoring = async (candidateId, force = false) => {
    try {
      await fetch(`${API}/ta/scoring/trigger`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ candidate_id: candidateId, force_rescore: force }),
      });
      setTimeout(fetchQueue, 2000);
    } catch { /* silent */ }
  };

  const handleCopyQALink = (candidate) => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const qaUrl = `${baseUrl}/ta-assessment/${candidate.unique_token}?qa=true&admin_token=${token}`;
    navigator.clipboard.writeText(qaUrl).then(() => {
      toast.success(`QA link copied for ${candidate.full_name}`);
    }).catch(() => {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = qaUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(`QA link copied for ${candidate.full_name}`);
    });
  };

  const handleOpenQALink = (candidate) => {
    const qaUrl = `/ta-assessment/${candidate.unique_token}?qa=true&admin_token=${token}`;
    window.open(qaUrl, "_blank");
  };

  const handleViewDetails = async (candidate) => {
    setSelectedCandidate(candidate);
    setLoadingDetail(true);
    setScoringDetail(null);
    setIntegrityReport(null);
    setVoiceRecordings(null);
    try {
      const [scoreRes, intRes, voiceRes] = await Promise.all([
        fetch(`${API}/ta/scoring/results/${candidate.candidate_id}`),
        fetch(`${API}/ta/integrity/report/${candidate.candidate_id}`),
        fetch(`${API}/assessment/admin/voice-recordings/${candidate.candidate_id}`),
      ]);
      const [scoreData, intData, voiceData] = await Promise.all([
        scoreRes.json(), intRes.json(), voiceRes.json(),
      ]);
      setScoringDetail(scoreData);
      setIntegrityReport(intData);
      setVoiceRecordings(voiceData);
    } catch { /* silent */ } finally { setLoadingDetail(false); }
  };

  const handleCloseDetail = () => {
    setSelectedCandidate(null);
    setScoringDetail(null);
    setIntegrityReport(null);
    setVoiceRecordings(null);
  };

  const [showSilentOnly, setShowSilentOnly] = useState(false);

  const filteredCandidates = (queue?.candidates || []).filter((c) => {
    if (showSilentOnly && !c.has_silent_recordings) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      c.full_name?.toLowerCase().includes(s) ||
      c.email?.toLowerCase().includes(s) ||
      c.assessment_type?.toLowerCase().includes(s) ||
      c.scoring_status?.toLowerCase().includes(s)
    );
  });

  const detailTabs = [
    { id: "scoring", label: "Scoring Review", icon: BarChart3 },
    { id: "voice", label: "Voice Recordings", icon: Mic },
    { id: "integrity", label: "Integrity", icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="ta-admin-dashboard">
      {/* Header */}
      <header className="bg-[#1B3A5C] text-white px-6 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate("/admin")}
              className="text-white/70 hover:text-white hover:bg-white/10"
              data-testid="ta-admin-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
            <div>
              <h1 className="text-lg font-semibold">TA/HOS Assessment Manager</h1>
              <p className="text-xs text-white/60">Scoring, Voice Review & Integrity</p>
            </div>
          </div>
          <Button
            variant="ghost" size="sm" onClick={fetchQueue}
            className="text-white/70 hover:text-white hover:bg-white/10"
            data-testid="ta-admin-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-6">
        {/* Summary Cards */}
        {queue?.summary && (
          <div className="grid grid-cols-5 gap-4 mb-6">
            {[
              { label: "Total", value: queue.summary.total, color: "text-[#1B3A5C]", bg: "bg-[#1B3A5C]/5" },
              { label: "Scored", value: queue.summary.scored, color: "text-emerald-600", bg: "bg-emerald-50" },
              { label: "Queued", value: queue.summary.queued, color: "text-amber-600", bg: "bg-amber-50" },
              { label: "Scoring", value: queue.summary.scoring, color: "text-blue-600", bg: "bg-blue-50" },
              { label: "Errors", value: queue.summary.errors, color: "text-red-600", bg: "bg-red-50" },
            ].map((stat) => (
              <Card key={stat.label} className={stat.bg}>
                <CardContent className="py-4 text-center">
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Search + Filter */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search candidates by name, email, type, or status..."
              className="pl-10"
              data-testid="ta-admin-search"
            />
          </div>
          <Button
            variant={showSilentOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setShowSilentOnly(!showSilentOnly)}
            className={showSilentOnly ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}
            data-testid="ta-admin-silent-filter"
          >
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
            Silent Recordings
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#00AEEF]" />
          </div>
        ) : selectedCandidate ? (
          /* ─── Detail View ─── */
          <div data-testid="ta-admin-detail-view">
            {/* Detail Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={handleCloseDetail} data-testid="ta-admin-detail-back">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to List
                </Button>
                <div>
                  <h2 className="text-lg font-semibold text-[#1B3A5C]">{selectedCandidate.full_name}</h2>
                  <p className="text-xs text-gray-500">
                    {selectedCandidate.email} &middot;{" "}
                    <span className={selectedCandidate.assessment_type === "technical_assessment" ? "text-[#00AEEF]" : "text-purple-600"}>
                      {selectedCandidate.assessment_type === "technical_assessment" ? "Technical Assessment" : "Hands-On Simulation"}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => window.open(`${API}/assessment/admin/voice-download-all/${selectedCandidate.candidate_id}`, "_blank")}
                  data-testid="ta-admin-download-all-voice"
                >
                  <FileDown className="w-4 h-4 mr-1" /> Download All Voice (ZIP)
                </Button>
                {selectedCandidate.status === "completed" && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => handleTriggerScoring(selectedCandidate.candidate_id, selectedCandidate.scoring_status === "scored")}
                    className="text-[#00AEEF] border-[#00AEEF]/30"
                    data-testid="ta-admin-rescore-btn"
                  >
                    <BarChart3 className="w-4 h-4 mr-1" /> {selectedCandidate.scoring_status === "scored" ? "Re-Score" : "Score"}
                  </Button>
                )}
              </div>
            </div>

            {loadingDetail ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#00AEEF]" />
              </div>
            ) : (
              <>
                {/* Detail Tabs */}
                <div className="flex gap-1 mb-4 border-b">
                  {detailTabs.map((tab) => {
                    const count = tab.id === "voice" && voiceRecordings
                      ? Object.keys(voiceRecordings.recordings_by_question || {}).length
                      : null;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setDetailTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                          detailTab === tab.id
                            ? "border-[#00AEEF] text-[#00AEEF]"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                        data-testid={`ta-detail-tab-${tab.id}`}
                      >
                        <tab.icon className="w-4 h-4" />
                        {tab.label}
                        {count != null && count > 0 && (
                          <span className="ml-1 text-[10px] bg-[#00AEEF]/10 text-[#00AEEF] px-1.5 py-0.5 rounded-full">{count}</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Tab Content */}
                {detailTab === "scoring" && <ScoringReviewPanel data={scoringDetail} />}
                {detailTab === "voice" && <VoiceRecordingsPanel data={voiceRecordings} candidateId={selectedCandidate.candidate_id} />}
                {detailTab === "integrity" && <IntegrityReviewPanel data={integrityReport} />}
              </>
            )}
          </div>
        ) : (
          /* ─── Candidate List ─── */
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="ta-admin-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Candidate</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Scoring</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCandidates.map((c) => (
                    <tr
                      key={c.candidate_id}
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleViewDetails(c)}
                      data-testid={`ta-admin-row-${c.candidate_id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium text-[#3b3b3b]">{c.full_name}</p>
                          {c.has_silent_recordings && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" title="Has silent recordings saved after warning">
                              <AlertTriangle className="w-3 h-3" /> Silent
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{c.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          c.assessment_type === "technical_assessment"
                            ? "bg-[#00AEEF]/10 text-[#00AEEF]"
                            : "bg-purple-100 text-purple-700"
                        }`}>
                          {c.assessment_type === "technical_assessment" ? "TA" : "HOS"}
                        </span>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                      <td className="px-4 py-3"><ScoringBadge status={c.scoring_status} /></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 px-2"
                            onClick={(e) => { e.stopPropagation(); handleViewDetails(c); }}
                            data-testid={`ta-admin-view-${c.candidate_id}`}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" /> Review
                          </Button>
                          {c.unique_token && (
                            <>
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-amber-600"
                                onClick={(e) => { e.stopPropagation(); handleOpenQALink(c); }}
                                title="Open QA preview in new tab"
                                data-testid={`ta-admin-qa-open-${c.candidate_id}`}
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-gray-400"
                                onClick={(e) => { e.stopPropagation(); handleCopyQALink(c); }}
                                title="Copy QA link to clipboard"
                                data-testid={`ta-admin-qa-copy-${c.candidate_id}`}
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                          {c.status === "completed" && c.scoring_status !== "scoring" && (
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-[#00AEEF]"
                              onClick={(e) => { e.stopPropagation(); handleTriggerScoring(c.candidate_id, c.scoring_status === "scored"); }}
                              data-testid={`ta-admin-score-${c.candidate_id}`}
                            >
                              <BarChart3 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredCandidates.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">No candidates found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   SCORING REVIEW PANEL
   ════════════════════════════════════════════════════════ */

function ScoringReviewPanel({ data }) {
  const [expandedDim, setExpandedDim] = useState(null);
  if (!data) return null;
  const result = data.result;

  if (!result) {
    return (
      <Card data-testid="scoring-review-empty">
        <CardContent className="py-12 text-center">
          <BarChart3 className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">
            {data.scoring_status === "not_started" ? "Not yet scored" : `Status: ${data.scoring_status}`}
          </p>
        </CardContent>
      </Card>
    );
  }

  const dims = result.dimension_scores || [];
  const justification = result.justification || {};

  return (
    <div className="space-y-4" data-testid="scoring-review-panel">
      {/* Composite Score Header */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1">Composite Score</p>
              <p className={`text-3xl font-bold ${scoreColor(result.composite_score)}`}>
                {result.final_composite || result.composite_score}
                <span className="text-sm text-gray-400 font-normal">/100</span>
              </p>
            </div>
            <div className="text-right text-xs text-gray-500">
              <p>Rubric: {result.rubric_version || "—"}</p>
              <p>Scored: {result.scored_at ? new Date(result.scored_at).toLocaleString() : "—"}</p>
              {result.override_by && (
                <p className="text-amber-600 mt-1">Overridden by {result.override_by}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dimension Scores with Justifications */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Dimension Scores & Justifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {dims.map((dim, idx) => {
            const isExpanded = expandedDim === idx;
            const j = dim.justification || {};
            const hasJustification = j.evidence_from_response || j.what_was_correct || j.what_was_missed || j.score_rationale;
            return (
              <div key={idx} className="border rounded-lg overflow-hidden" data-testid={`scoring-dimension-${idx}`}>
                {/* Dimension Header Row */}
                <button
                  onClick={() => setExpandedDim(isExpanded ? null : idx)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                  data-testid={`scoring-dimension-toggle-${idx}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-800 truncate">{dim.dimension}</span>
                    <span className="text-[10px] text-gray-400 shrink-0">{((dim.weight || 0) * 100).toFixed(0)}% weight</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${scoreBgColor(dim.score)}`}
                        style={{ width: `${Math.min(dim.score, 100)}%` }}
                      />
                    </div>
                    <span className={`text-sm font-bold w-8 text-right ${scoreColor(dim.score)}`}>{dim.score}</span>
                    {hasJustification && (
                      isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Justification */}
                {isExpanded && hasJustification && (
                  <div className="px-4 pb-4 pt-1 border-t bg-gray-50/50 space-y-3" data-testid={`scoring-justification-${idx}`}>
                    {j.evidence_from_response && (
                      <JustificationBlock
                        icon={Quote} label="Evidence from Response" color="blue"
                        text={j.evidence_from_response}
                        testId={`justification-evidence-${idx}`}
                      />
                    )}
                    {j.what_was_correct && (
                      <JustificationBlock
                        icon={CheckSquare} label="What Was Correct" color="emerald"
                        text={j.what_was_correct}
                        testId={`justification-correct-${idx}`}
                      />
                    )}
                    {j.what_was_missed && (
                      <JustificationBlock
                        icon={XSquare} label="What Was Missed" color="red"
                        text={j.what_was_missed}
                        testId={`justification-missed-${idx}`}
                      />
                    )}
                    {j.score_rationale && (
                      <JustificationBlock
                        icon={MessageSquare} label="Score Rationale" color="amber"
                        text={j.score_rationale}
                        testId={`justification-rationale-${idx}`}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Narrative Summary */}
      {result.narrative_summary && (
        <Card data-testid="scoring-narrative">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#00AEEF]" /> Narrative Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{result.narrative_summary}</p>
          </CardContent>
        </Card>
      )}

      {/* Strengths & Development Areas */}
      <div className="grid grid-cols-2 gap-4">
        {justification.strengths?.length > 0 && (
          <Card data-testid="scoring-strengths">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-emerald-700">Strengths</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1.5">
                {justification.strengths.map((s, i) => (
                  <li key={i} className="text-xs text-gray-700 flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
        {justification.development_areas?.length > 0 && (
          <Card data-testid="scoring-development-areas">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-amber-700">Development Areas</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1.5">
                {justification.development_areas.map((d, i) => (
                  <li key={i} className="text-xs text-gray-700 flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   VOICE RECORDINGS PANEL
   ════════════════════════════════════════════════════════ */

function VoiceRecordingsPanel({ data, candidateId }) {
  if (!data) return null;
  const recordingsByQ = data.recordings_by_question || {};
  const questionIds = Object.keys(recordingsByQ);

  if (questionIds.length === 0) {
    return (
      <Card data-testid="voice-recordings-empty">
        <CardContent className="py-12 text-center">
          <Mic className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No voice recordings for this candidate</p>
        </CardContent>
      </Card>
    );
  }

  const totalRecordings = questionIds.reduce((sum, q) => sum + recordingsByQ[q].length, 0);
  const activeRecordings = questionIds.reduce(
    (sum, q) => sum + recordingsByQ[q].filter((r) => !r.deleted_by_candidate).length, 0
  );
  const deletedRecordings = totalRecordings - activeRecordings;
  const totalDuration = questionIds.reduce(
    (sum, q) => sum + recordingsByQ[q].reduce((s, r) => s + (r.duration_seconds || 0), 0), 0
  );

  return (
    <div className="space-y-4" data-testid="voice-recordings-panel">
      {/* Voice Summary */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Questions", value: questionIds.length, color: "text-[#1B3A5C]" },
          { label: "Active Recordings", value: activeRecordings, color: "text-emerald-600" },
          { label: "Deleted", value: deletedRecordings, color: "text-red-500" },
          { label: "Total Duration", value: formatDuration(totalDuration), color: "text-[#00AEEF]" },
        ].map((stat) => (
          <Card key={stat.label} className="bg-white">
            <CardContent className="py-3 text-center">
              <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
              <p className="text-[10px] text-gray-500">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recordings grouped by Question */}
      {questionIds.map((qId) => (
        <QuestionRecordingsCard key={qId} questionId={qId} recordings={recordingsByQ[qId]} />
      ))}
    </div>
  );
}

function QuestionRecordingsCard({ questionId, recordings }) {
  const active = recordings.filter((r) => !r.deleted_by_candidate);
  const deleted = recordings.filter((r) => r.deleted_by_candidate);
  const [showDeleted, setShowDeleted] = useState(false);

  return (
    <Card data-testid={`voice-question-${questionId}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Mic className="w-4 h-4 text-[#00AEEF]" />
            <span className="font-mono text-xs">{questionId}</span>
          </span>
          <span className="text-xs text-gray-400">{active.length} recording{active.length !== 1 ? "s" : ""}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {active.map((rec) => (
          <RecordingRow key={rec.id} recording={rec} />
        ))}
        {deleted.length > 0 && (
          <>
            <button
              onClick={() => setShowDeleted(!showDeleted)}
              className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-1"
              data-testid={`voice-show-deleted-${questionId}`}
            >
              {showDeleted ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {deleted.length} deleted recording{deleted.length !== 1 ? "s" : ""}
            </button>
            {showDeleted && deleted.map((rec) => (
              <RecordingRow key={rec.id} recording={rec} isDeleted />
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function RecordingRow({ recording, isDeleted = false }) {
  const [playing, setPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  const audioSrc = `${API}/assessment/voice-audio/${recording.audio_url}`;
  const knownDuration = recording.duration_seconds || 0;

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      setAudioError(false);
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => setAudioError(true));
      setPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current || !knownDuration) return;
    const pct = Math.min((audioRef.current.currentTime / knownDuration) * 100, 100);
    setProgress(pct);
    // Force stop if we exceed known duration (WebM duration: Infinity workaround)
    if (audioRef.current.currentTime >= knownDuration + 1) {
      audioRef.current.pause();
      setPlaying(false);
      setProgress(100);
    }
  };

  const handleEnded = () => { setPlaying(false); setProgress(100); };
  const handleError = () => { setPlaying(false); setAudioError(true); };

  const downloadUrl = `${API}/assessment/admin/voice-download/${recording.id}`;

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${
        isDeleted ? "bg-red-50/50 border-red-200/50 opacity-60" : "bg-gray-50 border-gray-200"
      }`}
      data-testid={`voice-recording-${recording.id}`}
    >
      {/* Play Button */}
      <button
        onClick={togglePlay}
        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${
          audioError ? "bg-red-100 text-red-500" :
          playing ? "bg-[#00AEEF] text-white" : "bg-[#00AEEF]/10 text-[#00AEEF] hover:bg-[#00AEEF]/20"
        }`}
        data-testid={`voice-play-${recording.id}`}
        disabled={isDeleted && !recording.audio_url}
      >
        {audioError ? <XCircle className="w-4 h-4" /> : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>

      <audio
        ref={audioRef}
        src={audioSrc}
        onEnded={handleEnded}
        onError={handleError}
        onTimeUpdate={handleTimeUpdate}
        preload="none"
      />

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-700">
            Recording #{recording.recording_number}
          </span>
          <span className="text-[10px] text-gray-400">{formatDuration(recording.duration_seconds)}</span>
          {isDeleted && <span className="text-[10px] text-red-500 bg-red-100 px-1.5 py-0.5 rounded">Deleted by candidate</span>}
          {recording.saved_after_warning && (
            <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5" data-testid={`voice-silent-badge-${recording.id}`}>
              <AlertTriangle className="w-2.5 h-2.5" /> Silent — saved after warning
              {recording.mean_volume_db != null && <span className="ml-1 text-amber-500">({recording.mean_volume_db} dB)</span>}
            </span>
          )}
        </div>
        {playing && knownDuration > 0 && (
          <div className="w-full h-1 bg-gray-200 rounded-full mt-1.5 overflow-hidden">
            <div className="h-full bg-[#00AEEF] rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        )}
        {recording.recorded_at && (
          <p className="text-[10px] text-gray-400 mt-0.5">
            {new Date(recording.recorded_at).toLocaleString()}
          </p>
        )}
      </div>

      {/* Download */}
      <a
        href={downloadUrl} target="_blank" rel="noopener noreferrer"
        className="shrink-0 p-1.5 rounded hover:bg-gray-200 transition-colors"
        title="Download recording"
        data-testid={`voice-download-${recording.id}`}
      >
        <Download className="w-4 h-4 text-gray-500" />
      </a>
    </div>
  );
}


/* ════════════════════════════════════════════════════════
   INTEGRITY REVIEW PANEL
   ════════════════════════════════════════════════════════ */

function IntegrityReviewPanel({ data }) {
  if (!data) return null;
  const summary = data.summary;
  if (!summary) {
    return (
      <Card data-testid="integrity-review-empty">
        <CardContent className="py-12 text-center">
          <Shield className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No integrity data available</p>
        </CardContent>
      </Card>
    );
  }

  const hasFlags = summary.flag_count > 0;

  return (
    <div className="space-y-4" data-testid="integrity-review-panel">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Shield className={`w-4 h-4 ${hasFlags ? "text-amber-500" : "text-emerald-500"}`} />
              Integrity Summary
            </span>
            {hasFlags ? (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                {summary.flag_count} flag{summary.flag_count !== 1 ? "s" : ""}
              </span>
            ) : (
              <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Clean</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: "Tab Switches", value: summary.total_tab_switches },
              { label: "Paste Events", value: summary.total_paste_events },
              { label: "Typing Ratio", value: summary.overall_typing_ratio != null ? `${(summary.overall_typing_ratio * 100).toFixed(0)}%` : "N/A" },
              { label: "Time Away", value: `${summary.total_time_away_seconds || 0}s` },
            ].map((stat) => (
              <div key={stat.label} className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-[#1B3A5C]">{stat.value}</p>
                <p className="text-[10px] text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>

          {summary.integrity_flags?.length > 0 && (
            <div className="space-y-1.5 mt-4">
              {summary.integrity_flags.map((flag, idx) => (
                <div
                  key={idx}
                  className={`flex items-start gap-2 text-xs px-3 py-2 rounded-lg ${
                    flag.severity === "critical" ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-700 border border-amber-200"
                  }`}
                  data-testid={`integrity-flag-${idx}`}
                >
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>{flag.detail}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ════════════════════════════════════════════════════════ */

function JustificationBlock({ icon: Icon, label, color, text, testId }) {
  const colorMap = {
    blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800", icon: "text-blue-500" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", icon: "text-emerald-500" },
    red: { bg: "bg-red-50", border: "border-red-200", text: "text-red-800", icon: "text-red-500" },
    amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", icon: "text-amber-500" },
  };
  const c = colorMap[color] || colorMap.blue;
  return (
    <div className={`${c.bg} ${c.border} border rounded-lg p-3`} data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 ${c.icon}`} />
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${c.icon}`}>{label}</span>
      </div>
      <p className={`text-xs ${c.text} leading-relaxed`}>{text}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    completed: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", label: "Completed" },
    not_started: { icon: Clock, color: "text-gray-500", bg: "bg-gray-100", label: "Not Started" },
    in_progress: { icon: Clock, color: "text-blue-600", bg: "bg-blue-50", label: "In Progress" },
    locked: { icon: Clock, color: "text-gray-500", bg: "bg-gray-100", label: "Locked" },
  };
  const c = config[status] || config.not_started;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${c.bg} ${c.color}`}>
      <c.icon className="w-3 h-3" /> {c.label}
    </span>
  );
}

function ScoringBadge({ status }) {
  const config = {
    scored: { color: "text-emerald-600", bg: "bg-emerald-50", label: "Scored" },
    queued: { color: "text-amber-600", bg: "bg-amber-50", label: "Queued" },
    scoring: { color: "text-blue-600", bg: "bg-blue-50", label: "Scoring..." },
    error: { color: "text-red-600", bg: "bg-red-50", label: "Error" },
    not_started: { color: "text-gray-500", bg: "bg-gray-100", label: "Pending" },
  };
  const c = config[status] || config.not_started;
  return <span className={`text-xs px-2 py-0.5 rounded-full ${c.bg} ${c.color}`}>{c.label}</span>;
}

function scoreColor(score) {
  if (score >= 70) return "text-emerald-600";
  if (score >= 50) return "text-[#00AEEF]";
  if (score >= 25) return "text-amber-600";
  return "text-red-600";
}

function scoreBgColor(score) {
  if (score >= 70) return "bg-emerald-500";
  if (score >= 50) return "bg-[#00AEEF]";
  if (score >= 25) return "bg-amber-500";
  return "bg-red-500";
}

function formatDuration(seconds) {
  if (!seconds && seconds !== 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}
